import React from 'react'
import playButtonIcon from 'images/play-button.svg'
import pauseButtonIcon from 'images/pause-button.svg'
import { useSimpleAudioController } from 'hooks/use-simple-audio-controller'
import { getMessageBackgroundColor } from 'utils/message-colors'

interface VoiceMemoCardProps {
  attachment: {
    id: number
    filename: string
    size?: number
    url: string
    mime_type: string
    metadata?: {
      type?: string
      duration?: number
      waveform_data?: number[]
    }
  }
  isFromMe?: boolean
  service?: string
}

const VoiceMemoCard: React.FC<VoiceMemoCardProps> = ({
  attachment,
  isFromMe = true,
  service = 'iMessage',
}) => {
  const metadataDuration = attachment.metadata?.duration
    ? attachment.metadata.duration / 1000
    : undefined

  const { isPlaying, togglePlayback, getProgressRatio, getDisplayTime } =
    useSimpleAudioController({
      audioUrl: attachment.url,
      duration: metadataDuration,
      audioId: `voice-memo-${attachment.id}`,
    })
  const backgroundColor = getMessageBackgroundColor(isFromMe, service)

  return (
    <div
      className="d-flex flex-column"
      style={{
        gap: 'clamp(8px, 2vw, 12px)',
        borderTopLeftRadius: 'clamp(16px, 4vw, 24px)',
        borderTopRightRadius: 'clamp(16px, 4vw, 24px)',
        borderBottomLeftRadius: isFromMe ? 'clamp(16px, 4vw, 24px)' : '0px',
        borderBottomRightRadius: isFromMe ? '0px' : 'clamp(16px, 4vw, 24px)',
        backgroundColor: backgroundColor,
        padding: 'clamp(12px, 3vw, 16px)',
      }}
    >
      <div
        className="d-flex align-items-center"
        style={{ gap: 'clamp(8px, 2vw, 16px)' }}
      >
        <button
          type="button"
          className="d-flex align-items-center justify-content-center rounded-circle border-0"
          style={{
            width: 'clamp(40px, 10vw, 50px)',
            height: 'clamp(40px, 10vw, 50px)',
            backgroundColor: isFromMe ? 'rgba(255, 255, 255, 0.2)' : '#65c466',
            flexShrink: 0,
            cursor: 'pointer',
          }}
          onClick={togglePlayback}
        >
          <img
            src={isPlaying ? pauseButtonIcon : playButtonIcon}
            alt={isPlaying ? 'Pause' : 'Play'}
            style={{
              width: 'clamp(16px, 4vw, 20px)',
              height: 'clamp(16px, 4vw, 20px)',
              filter: isFromMe
                ? 'brightness(0) invert(1)'
                : 'brightness(0) invert(1)',
            }}
          />
        </button>
        <div
          className="flex-grow-1 d-flex align-items-center overflow-hidden px-1"
          style={{
            height: 'clamp(30px, 8vw, 40px)',
            gap: 'clamp(1px, 0.2vw, 2px)',
          }}
        >
          {/* Waveform bars */}
          {(
            attachment.metadata?.waveform_data || [
              12, 8, 16, 20, 14, 18, 10, 22, 16, 12, 24, 18, 14, 20, 12, 16, 8,
              18, 22, 14, 16, 12, 20, 18, 14, 22, 16, 12, 18, 20, 14, 16, 12,
              18, 22, 14, 20, 16, 12, 18, 20, 14, 16, 12, 18, 22, 14, 20, 16,
              12, 18, 20, 14, 16, 12, 18, 22, 14, 20, 16,
            ]
          )
            .slice(
              0,
              window.innerWidth > 768 ? 80 : window.innerWidth > 480 ? 60 : 40
            )
            .map((val: number, i: number, arr: number[]) => {
              const maxHeight =
                window.innerWidth > 768 ? 40 : window.innerWidth > 480 ? 32 : 24
              const height = Math.max(4, Math.min(maxHeight, val * 1.5))
              const progressRatio = getProgressRatio()
              const isPlayed = i / arr.length <= progressRatio
              return (
                <div
                  key={i}
                  style={{
                    width: 'clamp(1.5px, 0.3vw, 2.5px)',
                    height: `${height}px`,
                    backgroundColor: isFromMe ? '#ffffff' : '#333333',
                    opacity: isPlayed
                      ? isFromMe
                        ? 1
                        : 1
                      : isFromMe
                      ? 0.4
                      : 0.3,
                    borderRadius: '1px',
                    flexShrink: 0,
                  }}
                />
              )
            })}
        </div>
        <div
          className="fw-bold text-end"
          style={{
            fontSize: 'clamp(14px, 3.5vw, 16px)',
            minWidth: 'clamp(40px, 10vw, 50px)',
            color: isFromMe ? '#ffffff' : '#333333',
          }}
        >
          {getDisplayTime()}
        </div>
      </div>
    </div>
  )
}

export default VoiceMemoCard
